import { getVerdict, getVerdictStyles } from '@/helpers/ratingState';
import { getVerdictText } from '@/helpers/ratingState';
import {
  Verdict,
  OilColor,
  getOilColorStyles,
  getOilColorText,
  getVisualRatingStyles,
  getVisualRatingText,
} from '@/helpers/ratingState';
import React, { useMemo } from 'react';

const MAX_BADGE_TEXT_LENGTH = 30;
const truncateText = (text: string) =>
  text.length > MAX_BADGE_TEXT_LENGTH
    ? `${text.slice(0, MAX_BADGE_TEXT_LENGTH)}...`
    : text;
import clsx from 'clsx';
import { getStatusStyles } from './BadgeTypes';
import { BadgeStatusType, getStatusText } from './BadgeTypes';
import { VisualAssessmentRating } from '../VisualAssessmentBox';

export interface BadgeRatingProps {
  rating: number | Verdict;
  badgeText?: string;
  version?: 'v1' | 'v2';
  classNames?: string;
}
export interface BadgeProps {
  variant: BadgeStatusType;
  badgeText?: string;
  version?: 'v1' | 'v2';
  size?: 'md' | 'sm';
  classNames?: string;
}

export interface BadgeStatusProps {
  status: BadgeStatusType;
  className?: string;
  text?: string;
}

export interface BadgeOilStatusProps {
  color: OilColor;
  className?: string;
  text?: string;
}

export interface BadgeVisualRateProps {
  rating: VisualAssessmentRating;
  className?: string;
  text?: string;
}

/**
 * https://www.figma.com/design/L3SGf4hCCrolc6FOf0BB2E/Raport-Auta-2.0-WiFi-wireframes?node-id=1275-12918&t=LgKpQiXdL7EMNxdO-4
 *
 * @param rating - Ocena do wyświetlenia.
 */
export const BadgeRating = ({
  rating,
  version = 'v1',
  badgeText,
  classNames = '',
}: BadgeRatingProps) => {
  const verdict: Verdict = useMemo(() => {
    return getVerdict(rating);
  }, [rating]);

  return (
    <div
      className={`${classNames} gap-2.5 self-start whitespace-nowrap px-4 py-2 text-[10px] font-bold uppercase leading-3 tracking-wide lg:px-4 lg:text-xs ${getVerdictStyles(
        verdict,
        version,
      )} rounded-[50px]`}
    >
      {truncateText(badgeText || getVerdictText(verdict))}
    </div>
  );
};

/**
 * https://www.figma.com/design/L3SGf4hCCrolc6FOf0BB2E/Raport-Auta-2.0-WiFi-wireframes?node-id=1275-12918&t=LgKpQiXdL7EMNxdO-4
 *
 */
export const BadgeStatus = ({
  status,
  text,
  className = '',
}: BadgeStatusProps) => {
  return (
    <div
      className={`gap-2.5 self-start px-4 py-2 text-xs font-bold uppercase leading-3 tracking-wide md:whitespace-nowrap ${getStatusStyles(
        status,
      )} rounded-[50px] ${className}`}
    >
      {truncateText(text || getStatusText(status))}
    </div>
  );
};

/**
 * https://www.figma.com/design/L3SGf4hCCrolc6FOf0BB2E/Raport-Auta-2.0-WiFi-wireframes?node-id=1275-12928&t=LgKpQiXdL7EMNxdO-4
 *
 * Komponent do wyświetlania koloru oleju w postaci kartki.
 * @param color - Kolor oleju do wyświetlenia.
 * @param className - Dodatkowe klasy CSS.
 * @param text - Opcjonalny tekst do wyświetlenia zamiast domyślnego.
 * @returns Komponent do wyświetlania koloru oleju w postaci kartki.
 */
export const BadgeOilStatus = ({
  color,
  text,
  className = '',
}: BadgeOilStatusProps) => {
  return (
    <div
      className={`gap-2.5 self-start whitespace-nowrap px-4 py-2 text-xs font-bold uppercase leading-3 tracking-wide ${getOilColorStyles(
        color,
      )} rounded-[50px] ${className}`}
    >
      {truncateText(text || getOilColorText(color))}
    </div>
  );
};

/**
 * https://www.figma.com/design/L3SGf4hCCrolc6FOf0BB2E/Raport-Auta-2.0-WiFi-wireframes?node-id=1275-12918&t=DiDc0SqMamGtsBPW-4
 *
 * Komponent do wyświetlania wizualnej oceny zużycia w postaci kartki.
 * @param rating - Ocena wizualna do wyświetlenia.
 * @param className - Dodatkowe klasy CSS.
 * @param text - Opcjonalny tekst do wyświetlenia zamiast domyślnego.
 * @returns Komponent do wyświetlania wizualnej oceny zużycia w postaci kartki.
 */
export const BadgeVisualRate = ({
  rating,
  text,
  className = '',
}: BadgeVisualRateProps) => {
  return (
    <div
      className={`gap-2.5 self-start whitespace-nowrap px-4 py-2 font-inter text-[9px] md:text-[11px] font-bold uppercase tracking-wide ${getVisualRatingStyles(
        rating,
      )} rounded-[50px] ${className}`}
    >
      {truncateText(text || getVisualRatingText(rating))}
    </div>
  );
};

export const Badge = ({
  variant,
  version = 'v1',
  badgeText,
  size = 'md',
  classNames = '',
}: BadgeProps) => {
  return (
    <div
      className={clsx(
        classNames,
        'gap-2.5 self-start whitespace-nowrap',
        'font-bold uppercase leading-3 tracking-wide',
        'rounded-[50px]',
        size === 'sm' ? 'px-3 py-[5px] text-[10px]' : 'px-4 py-2 lg:px-4 text-[10px]  lg:text-xs',
        getStatusStyles(variant, version),
      )}
    >
      {badgeText ? truncateText(badgeText) : badgeText}
    </div>
  );
};